import React from 'react';
import { Paper, MenuList, MenuItem, ListItemIcon, ListItemText, Divider } from '@mui/material';
import {
  CreateNewFolder as CreateNewFolderIcon,
  CloudUpload as CloudUploadIcon,
  DriveFolderUpload as DriveFolderUploadIcon,
  AdminPanelSettings as AdminPanelSettingsIcon,
  FileDownload as FileDownloadIcon,
  Edit as EditIcon,
  FileCopy as FileCopyIcon,
  DriveFileMove as DriveFileMoveIcon,
  Star as StarIcon,
  Share as ShareIcon,
  Delete as DeleteIcon,
  DeleteForever as DeleteForeverIcon,
  Restore as RestoreIcon,
  Info as InfoIcon
} from '@mui/icons-material';

// 빈 공간 우클릭 메뉴 항목
const spaceMenuItems = [
  {
    id: 'new-folder',
    label: '새 폴더',
    icon: CreateNewFolderIcon,
    action: 'createFolder'
  },
  { type: 'divider' },
  {
    id: 'file-upload',
    label: '파일 업로드',
    icon: CloudUploadIcon,
    action: 'uploadFile'
  },
  {
    id: 'folder-upload',
    label: '폴더 업로드',
    icon: DriveFolderUploadIcon,
    action: 'uploadFolder'
  },
  {
    id: 'admin-upload',
    label: '파일 업로드(관리자)',
    icon: AdminPanelSettingsIcon,
    action: 'adminUpload'
  }
];

// 파일/폴더 우클릭 메뉴 항목
const fileMenuItems = [
  {
    id: 'download',
    label: '다운로드',
    icon: FileDownloadIcon,
    action: 'download'
  },
  {
    id: 'rename',
    label: '이름 바꾸기',
    icon: EditIcon,
    action: 'rename'
  },
  {
    id: 'copy',
    label: '사본 만들기',
    icon: FileCopyIcon,
    action: 'copy'
  },
  { type: 'divider' },
  {
    id: 'move',
    label: '이동',
    icon: DriveFileMoveIcon,
    action: 'move'
  },
  {
    id: 'favorite',
    label: '중요 문서함에 추가',
    icon: StarIcon,
    action: 'addToFavorites'
  },
  { type: 'divider' },
  {
    id: 'share',
    label: '공유',
    icon: ShareIcon,
    action: 'share'
  },
  {
    id: 'properties',
    label: '파일 세부정보',
    icon: InfoIcon,
    action: 'properties'
  },
  {
    id: 'delete',
    label: '휴지통으로 이동',
    icon: DeleteIcon,
    action: 'delete'
  }
];

// 휴지통 파일/폴더 우클릭 메뉴 항목
const trashFileMenuItems = [
  {
    id: 'restore',
    label: '복원',
    icon: RestoreIcon,
    action: 'restore'
  },
  {
    id: 'permanent-delete',
    label: '영구 삭제',
    icon: DeleteForeverIcon,
    action: 'permanentDelete'
  }
];

// 공유 문서함 파일/폴더 우클릭 메뉴 항목
const sharedFileMenuItems = [
  {
    id: 'download',
    label: '다운로드',
    icon: FileDownloadIcon,
    action: 'downloadShared'
  },
  {
    id: 'rename',
    label: '이름 바꾸기',
    icon: EditIcon,
    action: 'renameShared'
  },
  {
    id: 'copy-to-drive',
    label: '내 드라이브로 복사',
    icon: FileCopyIcon,
    action: 'copyToMyDrive'
  },
  { type: 'divider' },
  {
    id: 'add-to-favorites-shared',
    label: '중요 문서함에 추가',
    icon: StarIcon,
    action: 'addToFavoritesShared'
  },
  { type: 'divider' },
  {
    id: 'properties',
    label: '파일 세부정보',
    icon: InfoIcon,
    action: 'properties'
  },
  {
    id: 'delete-shared',
    label: '공유 삭제',
    icon: DeleteIcon,
    action: 'deleteShared'
  }
];

const ContextMenu = ({ 
  isOpen, 
  position, 
  menuType, // 'space' | 'file'
  onClose, 
  onItemClick,
  selectedItem,
  selectedItems = [],
  showTrash = false,
  viewType = 'myDrive' // 'myDrive' | 'shared' | 'trash'
}) => {
  // 자동 닫기 기능 비활성화 - DriveManagement에서 처리
  // useEffect(() => {
  //   if (!isOpen) return;

  //   const handleKeyDown = (event) => {
  //     if (event.key === 'Escape') {
  //       onClose();
  //     }
  //   };

  //   document.addEventListener('keydown', handleKeyDown);
  //   return () => document.removeEventListener('keydown', handleKeyDown);
  // }, [isOpen, onClose]);

  if (!isOpen) return null;

  // 휴지통에서는 빈 공간 메뉴 비활성화
  if (showTrash && menuType === 'space') return null;

  let menuItems;
  if (menuType === 'space') {
    menuItems = spaceMenuItems;
  } else if (showTrash) {
    menuItems = trashFileMenuItems;
  } else if (viewType === 'shared') {
    menuItems = sharedFileMenuItems;
  } else {
    // 내 드라이브의 경우 공유 메뉴 포함
    menuItems = [...fileMenuItems];

    // 복수 선택 시 "선택한 파일들 사본 만들기" 메뉴 추가
    if (selectedItems.length > 1) {
      const copyIndex = menuItems.findIndex(item => item.action === 'copy');
      if (copyIndex !== -1) {
        menuItems.splice(copyIndex, 0, {
          id: 'copy-selected',
          label: '선택한 파일들 사본 만들기',
          icon: FileCopyIcon,
          action: 'copySelected'
        });
      }
    }
  }

  // 화면 경계 체크 및 위치 조정
  const adjustPosition = (pos) => {
    const menuWidth = 220;
    const menuHeight = menuItems.length * 48 + 20;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let { x, y } = pos;

    // 오른쪽 경계 체크
    if (x + menuWidth > viewportWidth) {
      x = viewportWidth - menuWidth - 10;
    }

    // 아래쪽 경계 체크
    if (y + menuHeight > viewportHeight) {
      y = viewportHeight - menuHeight - 10;
    }

    // 최소값 보장
    x = Math.max(10, x);
    y = Math.max(10, y);

    return { x, y };
  };

  const adjustedPosition = adjustPosition(position);

  const handleItemClick = (item) => {
    if (item.type === 'divider') return;
    onItemClick(item.action, selectedItem);
    
    // reason을 전달할 수 있다면 전달
    if (typeof onClose === 'function') {
      if (onClose.length > 0) {
        onClose('menu-item-click');
      } else {
        onClose();
      }
    }
  };

  return (
    <>      
      {/* 컨텍스트 메뉴 */}
      <Paper
        data-context-menu
        elevation={8}
        sx={{
          position: 'fixed',
          left: `${adjustedPosition.x}px`,
          top: `${adjustedPosition.y}px`,
          zIndex: 1300,
          minWidth: 220,
          maxWidth: 300,
          borderRadius: 2,
          overflow: 'hidden'
        }}
      >
        <MenuList 
          dense 
          sx={{ 
            py: 1,
            '& .MuiMenuItem-root': {
              minHeight: 40,
              px: 2,
              py: 1,
              fontSize: '14px',
              '&:hover': {
                backgroundColor: '#f5f5f5'
              }
            }
          }}
        >
          {menuItems.map((item, index) => {
            if (item.type === 'divider') {
              return <Divider key={`divider-${index}`} sx={{ my: 0.5 }} />;
            }

            const IconComponent = item.icon;
            
            // 다중 선택 시 비활성화할 액션들
            let isDisabled = selectedItems.length > 1 && (item.action === 'rename' || item.action === 'renameShared' || item.action === 'properties');

            // 내 드라이브, 중요 문서함, 저장용량에서 복수 선택 시 추가 비활성화
            if (selectedItems.length > 1 && (viewType === 'myDrive' || viewType === 'bookmarks' || viewType === 'storage')) {
              const disabledActions = ['download', 'move', 'addToFavorites', 'share'];
              if (disabledActions.includes(item.action)) {
                isDisabled = true;
              }
            }

            // 휴지통에서 복수 선택 시 영구 삭제 비활성화 (서비스 미구현)
            if (selectedItems.length > 1 && viewType === 'trash' && item.action === 'permanentDelete') {
              isDisabled = true;
            }

            // 공유 문서함에서 복수 선택 시 모든 메뉴 비활성화
            if (selectedItems.length > 1 && viewType === 'shared') {
              isDisabled = true;
            }

            // 내 드라이브에서 폴더 업로드 비활성화
            if (viewType === 'myDrive' && item.action === 'uploadFolder') {
              isDisabled = true;
            }

            // 복수 선택 시 단일 파일 사본 만들기는 숨기고, 복수 사본 만들기만 표시
            if (selectedItems.length > 1 && item.action === 'copy') {
              return null; // 단일 파일 사본 만들기는 숨김
            }

            // 메뉴 라벨을 동적으로 변경
            let displayLabel = item.label;
            if (item.action === 'addToFavorites' && selectedItem?.bookmarkId) {
              displayLabel = '중요 문서함에서 제거';
            }

            // 휴지통에서 복수 선택 시 라벨 변경
            if (viewType === 'trash' && selectedItems.length > 1) {
              if (item.action === 'restore') {
                displayLabel = '선택한 항목들 복원';
              } else if (item.action === 'permanentDelete') {
                displayLabel = '선택한 항목들 영구 삭제';
              }
            }

            return (
              <MenuItem
                key={item.id}
                onClick={() => handleItemClick(item)}
                disabled={isDisabled}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  ...(isDisabled && {
                    color: 'text.disabled',
                    '&:hover': {
                      backgroundColor: 'transparent'
                    }
                  })
                }}
              >
                <ListItemIcon sx={{ minWidth: 'auto', mr: 0 }}>
                  <IconComponent
                    sx={{
                      fontSize: 18,
                      color: isDisabled ? 'text.disabled' : 'text.secondary'
                    }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={displayLabel}
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: '14px',
                      fontWeight: 400,
                      color: isDisabled ? 'text.disabled' : 'inherit'
                    }
                  }}
                />
              </MenuItem>
            );
          })}
        </MenuList>
      </Paper>
    </>
  );
};

export default ContextMenu;